import { useEffect, useRef, useState } from "react";
import IconRoundButton from "../../CommonButtons/IconRoundButton";
import PrimaryButton from "../../CommonButtons/PrimaryButton";
import FilteredSearchField from "../../CommonSearchComponents/FilteredSearchField";
import PrimaryWithIconButton from "../../CommonButtons/PrimaryWithIconButton";
import CommonSnoozeToggle from "../../CommonSnoozeToggle";
import CommonSingleandMultiView from "../../CommonSingleandMultiView";
import formatTotalBalance from "../../../../utils/formatTotalBalance";
import SecondaryAnimatedButton from "../../CommonButtons/SecondaryAnimated";
import SVGComponent from "../../SVGComponent";
import FormatAmount from "../../FormatAmount/FormatAmount";
import { ScreenSizeConfiguration } from "../../../../utils/configurationProvider";
import SecondaryRightIconSmallButton from "../../CommonButtons/SecondaryRightIconSmallButton";
import ICONS from "../../../../assets/Icons";
/**

@typedef {Object} ButtonProps

@property {string} btnText - The label text displayed on the button

@property {string} btnType - HTML button type (e.g., "button", "submit")

@property {string} enabledBgColor - Background color (CSS variable or hex) when active

@property {string} enabledTextColor - Text color (CSS variable or hex) when active

@property {Function} handleFunction - Callback function triggered on click

@property {boolean} isDisabled - Disables interaction and applies disabled styling

@property {string?} icon - URL or path to the icon image/SVG

@property {("left" | "right")?} iconToBeAlign - Position of the icon relative to text */

/**

@typedef {Object} IconComponent

@property {string} icon - URL or path to the icon SVG

@property {string} title - Tooltip text or accessible label

@property {string} enabledBgColor - Background color of the round button

@property {boolean} isLoading - Shows a loading spinner instead of the icon

@property {boolean} isDisabled - Disables the button

@property {Function} handleFunction - Callback function triggered on click

@property {string?} hoverOption - Hover style variant (e.g., "white") */

/**
@typedef {("primary" | "secondary")} BottomSubTextVaraint
@param {Object} props

@param {string | number} props.topNavHeader - Numeric value or string for the main header (e.g., balance). Formatted automatically.

@param {string} props.topNavSubText - Secondary descriptive text appearing below the main header.

@param {ButtonProps} props.topNavCtaBtn1Props - Configuration for the leftmost primary action button.

@param {ButtonProps} props.topNavCtaBtn2Props - Configuration for the secondary primary action button on the right.

@param {ButtonProps} props.topNavCtaBtnWithIcon1Props - Primary button with icon positioned on the left side.

@param {ButtonProps} props.BottomCtaBtnProps - Primary button with icon positioned on the right side.

@param {ButtonProps} props.topNavCtaBtnWithIcon2Props - Primary button with icon positioned on the right side.

@param {import("../../CommonSearchComponents/FilteredSearchField").FilteredSearchProps} props.filteredSearchProps - Configuration for the FilteredSearchField (See FilteredSearchField docs for details).

@param {Object} props.direction - direction of scroll to animate top nav.

@param {string} props.BottomHeader - Header text for the secondary info row below the main nav.

@param {string} props.BottomSubText - Sub-header text for the secondary info row.

@param {BottomSubTextVaraint} props.BottomSubTextVaraint - Sub-header text color for the secondary info row.

@param {IconComponent[]} props.iconComponentArray - Array of utility icon buttons (e.g., Refresh, Download) displayed on the right.

@param {Object} props.assignLabelProps - Props for the specialized "Assign Labels" animated button.

@param {Object} props.assignTagProps - Props for the specialized "Assign Tags" animated button.

@param {Object} props.toggleProps - Configuration for the Single/Multi view toggle component.

@param {Object} props.snoozeToggleProps - Configuration for the snooze view toggle component.

@param {boolean} props.isSelectionView - Master toggle to show the selection footer bar (used for bulk actions).

@param {string} props.selectionText - Text displayed during selection mode (e.g., "5 Accounts Selected").

@param {ButtonProps} props.selectionBtnProps - The primary action button to show when items are selected.

@param {bool} props.isFormattingNotRequired - If true then topnavheader will not be formatted as amount
@returns {JSX.Element} */
const TopNavComponent = ({
  topNavHeader,
  topNavSubText,
  topNavCtaBtn1Props,
  topNavCtaBtn2Props,
  filteredSearchProps,
  BottomHeader,
  BottomSubText,
  BottomSubTextVaraint,
  BottomCtaBtnProps,
  iconComponentArray,
  assignLabelProps,
  assignTagProps,
  toggleProps,
  snoozeToggleProps,
  isSelectionView,
  selectionText,
  selectionBtnProps,
  topNavCtaBtnWithIcon1Props,
  topNavCtaBtnWithIcon2Props,
  ispagination,
  customBottomPadding,
  isFormattingNotRequired,
  selectionIconComponentArray,
  selectionIconRoundBtnProps,
  direction,
  BottomCtaBtn2Props,
}) => {
  const [search, setSearch] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= ScreenSizeConfiguration.mobileScreen,
  );
  const mobileContainerRef = useRef(null);
  const topNavRef = useRef(null);

  const { isSearchDisabled = false } = filteredSearchProps || {};

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= ScreenSizeConfiguration.mobileScreen);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const ref = isMobile ? mobileContainerRef : containerRef;

      if (!ref.current) return;
      if (ref.current.contains(e.target)) return;

      topNavRef?.current?.blur();

      setSearch(false);
      setMobileSearch(false);
      setIsFilterOpen(false);
      setIsSortOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile]);

  useEffect(() => {
    setSearch(false);
    setMobileSearch(false);
    setIsFilterOpen(false);
    setIsSortOpen(false);
  }, [isMobile]);

  return (
    <>
      {/* MOBILE VIEW */}
      <div
        style={{
          bottom: ispagination
            ? "var(--pagination-height)"
            : customBottomPadding
              ? `var(--${customBottomPadding})`
              : "var(--height-base)",
        }}
        className={`fixed bg-primary-nav shadow-box-shadow-top p-padding-md rounded-t-border-radius-md w-full ${topNavCtaBtn1Props || topNavCtaBtnWithIcon1Props || topNavCtaBtnWithIcon2Props || topNavCtaBtn2Props ? "flex" : "hidden"} flex-col max-w-max-content-width item-center h-auto  Qsmd:hidden ${mobileSearch ? "pointer-events-none" : ""} z-z-index-fixed-ui  gap-x-gap-sm gap-y-top_md  justify-between`}
      >
        {mobileSearch && (
          <div className="absolute top-top-base left-left-base bg-PopUp-BackDrop opacity-dark-opacity pointer-events-none w-full h-full z-z-index-content Qsmd:z-z-index-header"></div>
        )}
        <div
          className={`w-full ${topNavCtaBtn1Props || topNavCtaBtn2Props ? "flex " : "hidden"}  gap-x-gap-sm `}
        >
          {topNavCtaBtn1Props ? (
            <PrimaryButton
              {...topNavCtaBtn1Props}
              fullWidthRequired={true}
            />
          ) : null}
          {topNavCtaBtn2Props ? (
            <PrimaryButton
              {...topNavCtaBtn2Props}
              fullWidthRequired={true}
            />
          ) : null}
        </div>
        <div
          className={`w-full ${topNavCtaBtnWithIcon1Props || topNavCtaBtnWithIcon2Props ? "flex " : "hidden"}  gap-x-gap-sm `}
        >
          {topNavCtaBtnWithIcon1Props ? (
            <PrimaryWithIconButton
              {...topNavCtaBtnWithIcon1Props}
              fullWidthRequired={true}
            />
          ) : null}

          {topNavCtaBtnWithIcon2Props ? (
            <PrimaryWithIconButton
              {...topNavCtaBtnWithIcon2Props}
              fullWidthRequired={true}
            />
          ) : null}
        </div>
      </div>
      <div
        className={`bg-primary-nav w-full flex flex-col overflow-visible  Qsmd:px-padding-sm px-padding-md shadow-box-shadow-bottom ${direction === "down" ? "absolute topnavanime" : direction === "up" ? "absolute topnavanimeup" : ""}`}
      >
        <div className="bg-primary-nav w-full flex flex-col  max-w-max-content-width mx-auto relative">
          {/* WEBVIEW */}
          <div
            style={{ overflowX: "clip" }}
            className={` w-full hidden Qsmd:flex flex-wrap max-w-max-content-width item-center min-h-topnavbar-min-height h-auto relative topnav-webview  gap-gap-sm  py-padding-sm justify-between`}
          >
            <div
              className={`${topNavCtaBtn1Props || topNavHeader || topNavSubText || topNavCtaBtnWithIcon1Props ? "flex" : "hidden"} ${search ? "opacity-opacity-none" : ""} transition-all duration-animation-fast-s ease-in-out gap-x-gap-sm  items-center  overflow-hidden   `}
            >
              <div className={`${topNavCtaBtn1Props ? "flex" : "hidden"}`}>
                <PrimaryButton {...topNavCtaBtn1Props} />
              </div>
              <div
                className={`${topNavCtaBtnWithIcon1Props ? "flex" : "hidden"}`}
              >
                {topNavCtaBtnWithIcon1Props ? (
                  <PrimaryWithIconButton {...topNavCtaBtnWithIcon1Props} />
                ) : null}
              </div>

              <div
                className={`${topNavHeader || topNavSubText ? "flex" : "hidden"}  flex-col gap-y-gap-xs`}
              >
                <p
                  title={
                    !isFormattingNotRequired
                      ? `INR ${FormatAmount({ price: topNavHeader })}`
                      : ""
                  }
                  className={`${!isFormattingNotRequired ? "text-settled" : "text-text-primary"} font-semibold text-font-sm`}
                >
                  {isFormattingNotRequired
                    ? topNavHeader
                    : "INR " +
                      formatTotalBalance(topNavHeader).amount +
                      " " +
                      formatTotalBalance(Number(topNavHeader)).abbr}
                </p>
                <p className="text-text-secondary text-font-xs font-normal">
                  {topNavSubText}
                </p>
              </div>
            </div>
            <div
              className={`${filteredSearchProps ? "flex" : "hidden"} flex-1 ${search ? "" : "relative"} min-h-topnavheight  min-w-search-min-width`}
            >
              <div
                ref={containerRef}
                onClick={(e) => {
                  if (isSearchDisabled) return;
                  if (isFilterOpen || isSortOpen) {
                  } else {
                    setSearch((prev) => !prev);
                  }
                }}
                className={`absolute inset-top-base flex items-center justify-center transition-all duration-animation-fast-s ease-in-out ${search ? "scale-full-scale opacity-base-opacity z-z-index-overlay" : `${topNavCtaBtn1Props || topNavHeader || topNavSubText || topNavCtaBtnWithIcon1Props || topNavCtaBtnWithIcon2Props || iconComponentArray || assignLabelProps || assignTagProps || toggleProps || snoozeToggleProps || topNavCtaBtn2Props ? "Qsmd:scale-x-full-scale-animate Qsmd:scale-y-full-scale scale-full-scale" : "scale-full-scale"}   z-z-index-header`}`}
              >
                {filteredSearchProps && !isMobile && (
                  <FilteredSearchField
                    {...filteredSearchProps}
                    isTopNavSearch={search}
                    isTopNav={true}
                    setTopNavSearch={(val) =>
                      setSearch((prev) => (prev === null ? true : val))
                    }
                    setIsFilterOpen={setIsFilterOpen}
                    setIsSortOpen={setIsSortOpen}
                    topNavRef={topNavRef}
                  />
                )}
              </div>
            </div>

            <div
              className={`${iconComponentArray || assignLabelProps || assignTagProps || topNavCtaBtnWithIcon2Props || toggleProps || snoozeToggleProps || topNavCtaBtn2Props ? "flex" : "hidden"} ${search ? "opacity-opacity-none" : ""} transition-all duration-animation-fast-s ease-in-out gap-x-gap-sm items-center   `}
            >
              {iconComponentArray?.map((item) => {
                return <IconRoundButton {...item} />;
              })}
              {assignLabelProps ? (
                <SecondaryAnimatedButton
                  {...assignLabelProps}
                  icon={ICONS?.LABEL}
                  text="Assign labels"
                />
              ) : null}
              {assignTagProps ? (
                <SecondaryAnimatedButton
                  {...assignTagProps}
                  icon={ICONS?.ASSIGN_TAG_WITHOUT_CIRCLE}
                  text="Assign tags"
                />
              ) : null}
              {toggleProps ? (
                <CommonSingleandMultiView {...toggleProps} />
              ) : null}
              {snoozeToggleProps ? (
                <CommonSnoozeToggle {...snoozeToggleProps} />
              ) : null}
              <div className={`${topNavCtaBtn2Props ? "flex" : "hidden"}`}>
                {topNavCtaBtn2Props ? (
                  <PrimaryButton {...topNavCtaBtn2Props} />
                ) : null}
              </div>
              <div
                className={`${topNavCtaBtnWithIcon2Props ? "flex" : "hidden"}`}
              >
                {topNavCtaBtnWithIcon2Props ? (
                  <PrimaryWithIconButton {...topNavCtaBtnWithIcon2Props} />
                ) : null}
              </div>
            </div>
          </div>

          {/* MOBILE VIEW */}
          <div
            style={{ overflowX: "clip" }}
            className={` w-full flex flex-col max-w-max-content-width item-center h-auto relative Qsmd:hidden  justify-between`}
          >
            <div
              className={`${filteredSearchProps ? "flex" : "hidden"} flex-1 relative min-h-topnavbar-min-height ${filteredSearchProps ? "pb-padding-sm" : "py-padding-sm"}   `}
            >
              <div
                ref={mobileContainerRef}
                onClick={(e) => {
                  if (isSearchDisabled) return;
                  if (isFilterOpen || isSortOpen) {
                  } else {
                    setMobileSearch(true);
                  }
                }}
                className={`absolute inset-top-base  flex items-center justify-center transition-all duration-animation-fast-s ease-in-out ${search ? "scale-full-scale opacity-base-opacity z-z-index-overlay" : "scale-x-full-scale-animate scale-y-full-scale"}   z-z-index-header`}
              >
                {filteredSearchProps && isMobile && (
                  <FilteredSearchField
                    {...filteredSearchProps}
                    isTopNavSearch={mobileSearch}
                    isTopNav={true}
                    setTopNavSearch={(val) =>
                      setMobileSearch((prev) => (prev === null ? true : val))
                    }
                    setIsFilterOpen={setIsFilterOpen}
                    setIsSortOpen={setIsSortOpen}
                    topNavRef={topNavRef}
                  />
                )}
              </div>
            </div>
            <div
              className={`${iconComponentArray || assignLabelProps || assignTagProps || toggleProps || snoozeToggleProps || topNavHeader || topNavSubText ? "flex" : "hidden"} gap-x-gap-md ${topNavHeader || topNavSubText ? " justify-between" : "justify-end"} ${filteredSearchProps ? "border-t-border border-primary-bg" : ""}  w-full pb-padding-sm`}
            >
              <div
                className={`${topNavHeader || topNavSubText ? "flex pt-padding-sm" : "hidden"}  flex-col gap-y-gap-xs`}
              >
                <p
                  title={
                    !isFormattingNotRequired
                      ? `INR ${FormatAmount({ price: topNavHeader })}`
                      : ""
                  }
                  className={`${!isFormattingNotRequired ? "text-settled" : "text-text-primary"} font-semibold text-font-sm`}
                >
                  {isFormattingNotRequired
                    ? topNavHeader
                    : "INR " +
                      formatTotalBalance(topNavHeader).amount +
                      " " +
                      formatTotalBalance(Number(topNavHeader)).abbr}
                </p>
                <p className="text-text-secondary text-font-xs font-normal">
                  {topNavSubText}
                </p>
              </div>

              <div
                className={`${iconComponentArray || assignLabelProps || assignTagProps || toggleProps || snoozeToggleProps ? "flex pt-top_sm" : "hidden"}  gap-x-gap-sm items-center   `}
              >
                {iconComponentArray?.map((item) => {
                  return <IconRoundButton {...item} />;
                })}
                {assignLabelProps ? (
                  <SecondaryAnimatedButton
                    {...assignLabelProps}
                    icon={ICONS?.LABEL}
                    text="Assign labels"
                  />
                ) : null}
                {assignTagProps ? (
                  <SecondaryAnimatedButton
                    {...assignTagProps}
                    icon={ICONS?.ASSIGN_TAG_WITHOUT_CIRCLE}
                    text="Assign tags"
                  />
                ) : null}
                {toggleProps ? (
                  <CommonSingleandMultiView {...toggleProps} />
                ) : null}
                {snoozeToggleProps ? (
                  <CommonSnoozeToggle {...snoozeToggleProps} />
                ) : null}
              </div>
            </div>
          </div>
          <div
            className={`flex-col Qsmd:flex-row py-padding-sm gap-y-gap-sm ${BottomCtaBtnProps || BottomCtaBtn2Props ? "gap-x-gap-sm" : ""} justify-between items-start Qsmd:items-center ${BottomHeader || BottomSubText || BottomCtaBtnProps || BottomCtaBtn2Props ? "flex" : "hidden"}  border-t-border border-primary-bg`}
          >
            <div
              className={`flex flex-col ${BottomHeader && BottomSubText ? "gap-y-gap-xs" : ""}`}
            >
              <p
                className={`text-text-primary text-font-sm font-normal ${BottomHeader ? "flex" : "hidden"}`}
              >
                {BottomHeader}
              </p>
              <p
                className={`${BottomSubTextVaraint === "primary" ? "text-text-primary" : "text-text-secondary"}  text-font-xs font-normal ${BottomSubText ? "flex" : "hidden"}`}
              >
                {BottomSubText}
              </p>
            </div>
            <div className="flex gap-x-gap-sm">
              {BottomCtaBtnProps ? (
                <SecondaryRightIconSmallButton
                  {...BottomCtaBtnProps}
                  iconToBeAlign="right"
                />
              ) : null}
              {BottomCtaBtn2Props ? (
                <div className=" border-l-border border-primary-bg"></div>
              ) : null}
              {BottomCtaBtn2Props ? (
                <SecondaryRightIconSmallButton
                  {...BottomCtaBtn2Props}
                  iconToBeAlign="right"
                />
              ) : null}
            </div>
          </div>
          <div
            className={` py-padding-sm flex-col Qsmd:flex-row gap-y-gap-sm items-start Qsmd:items-center  justify-between  border-t-border border-primary-bg ${isSelectionView ? "flex" : "hidden"}`}
          >
            <p className="text-text-primary text-font-xs font-normal">
              {selectionText}
            </p>
            <div className="flex gap-x-gap-sm">
              {selectionIconComponentArray?.map((item) => {
                return <IconRoundButton {...item} />;
              })}
              {selectionBtnProps ? (
                <SecondaryRightIconSmallButton
                  {...selectionBtnProps}
                  iconToBeAlign="right"
                />
              ) : null}
              {selectionIconRoundBtnProps ? (
                <IconRoundButton {...selectionIconRoundBtnProps} />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TopNavComponent;
